import * as Sentry from "@sentry/react";

// Google Translate (including Chrome's built-in page translation) replaces
// text nodes with <font> wrappers behind React's back. React still holds the
// original nodes, so its next insertBefore/removeChild against them throws a
// NotFoundError and the ErrorBoundary takes down the page.
// See https://github.com/facebook/react/issues/11538
//
// We only intervene when the DOM call would throw AND Google Translate has
// marked the document as translated; every other case goes through the
// native method untouched so genuine DOM bugs still surface.

const isGoogleTranslateActive = () => {
  const { classList } = document.documentElement;
  return (
    classList.contains("translated-ltr") || classList.contains("translated-rtl")
  );
};

const recordRecovery = (method: string) => {
  Sentry.addBreadcrumb({
    category: "translate-guard",
    level: "warning",
    message: `Recovered from Google Translate DOM mismatch in ${method}`,
  });
};

let installed = false;

export function installTranslateGuard() {
  if (installed || typeof Node !== "function") return;
  installed = true;

  const originalRemoveChild = Node.prototype.removeChild;
  const originalInsertBefore = Node.prototype.insertBefore;

  Node.prototype.removeChild = function <T extends Node>(
    this: Node,
    child: T,
  ): T {
    if (child.parentNode !== this && isGoogleTranslateActive()) {
      recordRecovery("removeChild");
      // React wants the node gone; remove it from wherever it lives now
      child.parentNode?.removeChild(child);
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (
      referenceNode &&
      referenceNode.parentNode !== this &&
      isGoogleTranslateActive()
    ) {
      recordRecovery("insertBefore");
      // If the reference was wrapped (e.g. in a <font>), insert before the
      // wrapper to keep the order; if it was detached entirely, append.
      let anchor: Node | null = referenceNode;
      while (anchor && anchor.parentNode !== this) {
        anchor = anchor.parentNode;
      }
      return originalInsertBefore.call(this, newNode, anchor) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
